import { useMemo, useState } from 'react';

import { useServiceCategoryOptions } from '../service-categories/api.js';
import {
  useAgreementRates,
  useCreateAgreementRate,
  useDeleteAgreementRate,
  useUpdateAgreementRate,
} from './api.js';

interface AgreementRatesSectionProps {
  agreementId: string;
  canEdit: boolean;
}

const currency = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });

export function AgreementRatesSection({
  agreementId,
  canEdit,
}: AgreementRatesSectionProps): JSX.Element {
  const ratesQuery = useAgreementRates(agreementId);
  const categoriesQuery = useServiceCategoryOptions();
  const createMutation = useCreateAgreementRate();
  const updateMutation = useUpdateAgreementRate();
  const deleteMutation = useDeleteAgreementRate();

  // Local edits to override inputs, keyed by service category id. A row is "dirty" while
  // present in this map; on blur we reconcile the value against the saved override.
  const [drafts, setDrafts] = useState<Record<string, string>>({});

  const categories = categoriesQuery.data ?? [];

  // Existing overrides indexed by service category id.
  const overrideByCategory = useMemo(() => {
    const map = new Map<string, { id: string; rate: string }>();
    for (const rate of ratesQuery.data?.data ?? []) {
      map.set(rate.serviceCategoryId, { id: rate.id, rate: rate.rate });
    }
    return map;
  }, [ratesQuery.data]);

  const saveOverride = (categoryId: string, raw: string): void => {
    const value = raw.trim();
    const existing = overrideByCategory.get(categoryId);
    if (!value) {
      // Cleared: delete any existing override.
      if (existing) deleteMutation.mutate({ agreementId, id: existing.id });
    } else if (existing) {
      if (existing.rate !== value) {
        updateMutation.mutate({ agreementId, id: existing.id, input: { rate: value } });
      }
    } else {
      createMutation.mutate({
        agreementId,
        input: { serviceCategoryId: categoryId, rate: value },
      });
    }
    setDrafts((prev) => {
      const next = { ...prev };
      delete next[categoryId];
      return next;
    });
  };

  return (
    <div className="rounded-lg border border-slate-200">
      <div className="border-b border-slate-200 px-3 py-2">
        <span className="text-sm font-medium text-slate-700">Service category rates</span>
      </div>

      {ratesQuery.isLoading || categoriesQuery.isLoading ? (
        <p className="px-3 py-2 text-sm text-slate-500">Loading rates…</p>
      ) : categories.length === 0 ? (
        <p className="px-3 py-2 text-sm text-slate-500">No service categories configured.</p>
      ) : (
        <table className="w-full text-left text-sm">
          <thead className="border-b border-slate-200 text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-3 py-2 font-medium">Service category</th>
              <th className="px-3 py-2 font-medium">Default rate</th>
              <th className="px-3 py-2 font-medium">Override rate</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {categories.map((category) => {
              const existing = overrideByCategory.get(category.id);
              const draft = drafts[category.id];
              const value = draft !== undefined ? draft : (existing?.rate ?? '');
              return (
                <tr key={category.id}>
                  <td className="px-3 py-2 font-medium text-slate-900">{category.name}</td>
                  <td className="px-3 py-2 text-slate-400">
                    {currency.format(Number(category.defaultRate))}
                  </td>
                  <td className="px-3 py-2">
                    <input
                      type="number"
                      min={0}
                      step="0.01"
                      disabled={!canEdit}
                      value={value}
                      placeholder="—"
                      onChange={(e) =>
                        setDrafts((prev) => ({ ...prev, [category.id]: e.target.value }))
                      }
                      onBlur={(e) => {
                        if (canEdit && draft !== undefined) {
                          saveOverride(category.id, e.target.value);
                        }
                      }}
                      className="w-32 rounded-lg border border-slate-300 px-2 py-1 text-sm text-slate-900 placeholder:text-slate-400 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 disabled:bg-slate-50 disabled:text-slate-500"
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}
